'use client';

import { useEffect, useRef } from 'react';

type LoggingHandler = (index: number, dataset: Record<string, string | undefined>) => void;

export const useSelectorScrollLogging = (selector: string, loggingHandler: LoggingHandler) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const handlerRef = useRef<LoggingHandler>(loggingHandler);

  handlerRef.current = loggingHandler;

  useEffect(() => {
    if (!ref.current) return;

    const targetTags = Array.from(ref.current.querySelectorAll(selector));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const currentIndex = targetTags.findIndex((t) => t === entry.target);
          if (currentIndex === -1) return;
          handlerRef.current?.(currentIndex, (entry.target as HTMLElement).dataset);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.2 },
    );
    targetTags.forEach((element, index) => {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;

      if (isVisible) {
        handlerRef.current?.(index, (element as HTMLElement).dataset);
      } else {
        observer.observe(element);
      }
    });
    return () => {
      observer.disconnect();
    };
  }, [selector]);

  return ref;
};
